import { Fragment, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

// ---- types (mirror the /security/* API shapes) ----
interface LockRow {
  type: string; id: string; email?: string; name?: string;
  generation?: number; lockUntil?: string | null; hard?: boolean;
  reason?: string | null; lockedBy?: string | null; incident?: string | null; lockedAt?: string | null;
}
interface EventRow {
  id: string; timestamp?: string; user_email?: string; entity_id?: string; entity_name?: string;
  ip_address?: string | null; changes_summary?: string; metadata?: any;
}
interface Stats {
  mode?: string;
  tracker?: { principals: number; ips: number; events: number };
  topPrincipals?: Array<{ key: string; records15m: number; classes: number; exports15m: number }>;
}

const PRINCIPAL_TYPES = [
  { value: 'dealer_contact', label: 'Dealer contact' },
  { value: 'dealer_sales_rep', label: 'Dealer sales rep' },
  { value: 'rep_firm_contact', label: 'Rep-firm contact' },
  { value: 'employee', label: 'Employee' },
  { value: 'sales_rep_user', label: 'Internal sales rep' },
  { value: 'legacy_user', label: 'Legacy / workstation user' },
];

function parseMeta(m: any): any {
  if (!m) return {};
  if (typeof m === 'string') { try { return JSON.parse(m); } catch { return {}; } }
  return m;
}
function fmt(ts?: string | null): string {
  if (!ts) return '—';
  const d = new Date(ts);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
}
function levelBadge(level?: string) {
  const map: Record<string, string> = {
    l1: 'bg-gray-100 text-gray-700', l2: 'bg-amber-100 text-amber-800',
    l3: 'bg-red-100 text-red-800', alert_only: 'bg-blue-100 text-blue-800',
  };
  return map[level || ''] || 'bg-gray-100 text-gray-600';
}
function sevBadge(sev?: string) {
  if (sev === 'high') return 'bg-red-100 text-red-800';
  if (sev === 'medium') return 'bg-amber-100 text-amber-800';
  return 'bg-gray-100 text-gray-600';
}

export default function SecurityPage() {
  const { user } = useAuth();
  const isAdmin = !!(user?.permissions?.all_access || user?.permissions?.factory_admin_access || user?.role === 'admin');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<string>('');
  const [stats, setStats] = useState<Stats>({});
  const [locks, setLocks] = useState<LockRow[]>([]);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);

  // manual controls
  const [mType, setMType] = useState('dealer_contact');
  const [mId, setMId] = useState('');
  const [mReason, setMReason] = useState('');
  const [mDuration, setMDuration] = useState('30');
  const [mHard, setMHard] = useState(false);

  // confirmation modal
  const [confirmState, setConfirmState] = useState<null | {
    title: string; body: string; requireReason?: boolean; danger?: boolean; run: (reason: string) => Promise<any>;
  }>(null);
  const [confirmReason, setConfirmReason] = useState('');
  const [actionResult, setActionResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const selfId = user?.id;

  async function loadAll() {
    setLoading(true); setError(null);
    try {
      const [modeRes, statsRes, locksRes, eventsRes] = await Promise.all([
        api.security.getMode().catch(() => ({ mode: '' })),
        api.security.getStats().catch(() => ({})),
        api.security.getLocks().catch(() => []),
        api.security.getEvents(100).catch(() => ({ data: [] })),
      ]);
      setMode((modeRes as any)?.mode || (statsRes as any)?.mode || '');
      setStats(statsRes as Stats);
      setLocks(Array.isArray(locksRes) ? locksRes : []);
      setEvents(((eventsRes as any)?.data) || (Array.isArray(eventsRes) ? eventsRes : []));
    } catch (e: any) {
      setError(e.message || 'Failed to load security data');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { if (isAdmin) loadAll(); else setLoading(false); }, [isAdmin]);

  // level tallies derived from the loaded events (real data, not fabricated)
  const levelCounts = useMemo(() => {
    const c = { l1: 0, l2: 0, l3: 0, alert_only: 0 };
    for (const ev of events) {
      const lvl = parseMeta(ev.metadata).level as keyof typeof c;
      if (lvl && lvl in c) c[lvl]++;
    }
    return c;
  }, [events]);
  const lastIncident = useMemo(() => (events[0]?.timestamp ? fmt(events[0].timestamp) : null), [events]);

  async function doAction(run: () => Promise<any>) {
    setBusy(true); setActionResult(null);
    try {
      const res = await run();
      setActionResult({ ok: true, message: res?.message || JSON.stringify(res) });
      await loadAll();
    } catch (e: any) {
      setActionResult({ ok: false, message: e.message || 'Action failed' });
    } finally {
      setBusy(false); setConfirmState(null); setConfirmReason('');
    }
  }

  const modeLabel = (mode || 'shadow').toUpperCase();
  const modeColor = mode === 'enforce' ? 'bg-red-600' : mode === 'off' ? 'bg-gray-500' : 'bg-amber-500';

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg shadow p-6 max-w-lg">
          <h1 className="text-xl font-semibold text-gray-900">Security</h1>
          <p className="mt-2 text-gray-600">You do not have access to the security dashboard. A factory security administrator is required.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Security</h1>
          <p className="text-sm text-gray-500">Authenticated anti-scraping monitoring &amp; session controls</p>
        </div>
        <button onClick={loadAll} className="px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50">Refresh</button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3">{error}</div>}
      {loading ? <div className="text-gray-500">Loading…</div> : (
      <>
        {/* ---- Overview ---- */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-xs uppercase text-gray-400 font-medium">Mode</div>
            <div className="mt-2 flex items-center gap-2">
              <span className={`inline-block w-2.5 h-2.5 rounded-full ${modeColor}`}></span>
              <span className="text-lg font-semibold text-gray-900">{modeLabel}</span>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-xs uppercase text-gray-400 font-medium">Locked accounts</div>
            <div className="mt-2 text-2xl font-bold text-gray-900">{locks.length}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-xs uppercase text-gray-400 font-medium">Recent events</div>
            <div className="mt-2 text-2xl font-bold text-gray-900">{events.length}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-xs uppercase text-gray-400 font-medium">Last incident</div>
            <div className="mt-2 text-sm font-medium text-gray-900">{lastIncident || '—'}</div>
          </div>
        </div>

        {mode !== 'enforce' && (
          <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-4 py-3 text-sm">
            <strong>Shadow mode.</strong> Security monitoring is active. Automatic account containment is not enabled.
          </div>
        )}

        {/* ---- Level activity + rolling stats ---- */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <h2 className="font-semibold text-gray-900 mb-3">Detection activity (recent events)</h2>
            <div className="grid grid-cols-4 gap-2 text-center">
              {([['L1', levelCounts.l1], ['L2', levelCounts.l2], ['L3', levelCounts.l3], ['Alert', levelCounts.alert_only]] as const).map(([k, v]) => (
                <div key={k} className="rounded-lg border border-gray-100 py-3">
                  <div className="text-xl font-bold text-gray-900">{v}</div>
                  <div className="text-xs text-gray-500">{k}</div>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-gray-400">L1 throttle events are monitored but not persisted as incidents.</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h2 className="font-semibold text-gray-900 mb-3">Rolling detection counters</h2>
            {stats.tracker ? (
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg border border-gray-100 py-3"><div className="text-xl font-bold">{stats.tracker.principals}</div><div className="text-xs text-gray-500">Principals tracked</div></div>
                <div className="rounded-lg border border-gray-100 py-3"><div className="text-xl font-bold">{stats.tracker.ips}</div><div className="text-xs text-gray-500">Source IPs</div></div>
                <div className="rounded-lg border border-gray-100 py-3"><div className="text-xl font-bold">{stats.tracker.events}</div><div className="text-xs text-gray-500">Window events</div></div>
              </div>
            ) : <div className="text-sm text-gray-400">No counter data available.</div>}
            {!!stats.topPrincipals?.length && (
              <div className="mt-3">
                <div className="text-xs uppercase text-gray-400 font-medium mb-1">Top read volume (15m)</div>
                <div className="space-y-1 max-h-40 overflow-auto">
                  {stats.topPrincipals.slice(0, 8).map((p) => (
                    <div key={p.key} className="flex justify-between text-sm border-b border-gray-50 py-1">
                      <span className="font-mono text-xs text-gray-600 truncate mr-2">{p.key}</span>
                      <span className="text-gray-900">{p.records15m} rec · {p.classes} cls · {p.exports15m} exp</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ---- Manual controls ---- */}
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="font-semibold text-gray-900 mb-3">Manual session &amp; account controls</h2>
          <p className="text-sm text-gray-500 mb-3">Scoped to a single principal. Available in every mode. Every action is audited server-side.</p>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col text-xs text-gray-500">Principal type
              <select value={mType} onChange={(e) => setMType(e.target.value)} className="mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900">
                {PRINCIPAL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>
            <label className="flex flex-col text-xs text-gray-500 flex-1 min-w-[260px]">Principal ID
              <input value={mId} onChange={(e) => setMId(e.target.value)} placeholder="UUID" className="mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
            </label>
            <label className="flex flex-col text-xs text-gray-500">Lock minutes
              <input value={mDuration} onChange={(e) => setMDuration(e.target.value)} className="mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm w-24" />
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
              <input type="checkbox" checked={mHard} onChange={(e) => setMHard(e.target.checked)} /> Hard lock
            </label>
          </div>
          <input value={mReason} onChange={(e) => setMReason(e.target.value)} placeholder="Reason (recorded in the audit trail)" className="mt-3 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          <div className="mt-3 flex flex-wrap gap-2">
            <button disabled={!mId} onClick={() => setConfirmState({
              title: 'Revoke all sessions', danger: true,
              body: `Revoke every active session for ${mType} ${mId}. Existing tokens stop working immediately; a fresh login is required.`,
              run: async () => api.security.revokeSessions(mType, mId),
            })} className="px-3 py-2 text-sm rounded-lg bg-gray-800 text-white disabled:opacity-40">Revoke sessions</button>
            <button disabled={!mId} onClick={() => setConfirmState({
              title: mHard ? 'Apply hard lock' : 'Apply temporary lock', danger: true, requireReason: true,
              body: `Lock ${mType} ${mId}${mHard ? ' (hard — no auto-expiry; a different admin must clear it)' : ` for ${mDuration || 30} minutes`}. This also revokes current sessions.`,
              run: async (reason) => api.security.lock(mType, mId, { reason, durationMinutes: Number(mDuration) || 30, hard: mHard }),
            })} className="px-3 py-2 text-sm rounded-lg bg-red-600 text-white disabled:opacity-40">Lock account</button>
            <button disabled={!mId} onClick={() => setConfirmState({
              title: 'Unlock account',
              body: `Clear the security lock on ${mType} ${mId}.`,
              run: async () => api.security.unlock(mType, mId),
            })} className="px-3 py-2 text-sm rounded-lg bg-white border border-gray-300 disabled:opacity-40">Unlock</button>
          </div>
          {actionResult && (
            <div className={`mt-3 rounded-lg px-3 py-2 text-sm ${actionResult.ok ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
              {actionResult.ok ? '✓ ' : '✕ '}{actionResult.message}
            </div>
          )}
        </div>

        {/* ---- Locked accounts ---- */}
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="font-semibold text-gray-900 mb-3">Locked accounts ({locks.length})</h2>
          {locks.length === 0 ? <div className="text-sm text-gray-400">No accounts are currently locked.</div> : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead><tr className="text-left text-gray-400 border-b">
                  <th className="py-2 pr-4">User</th><th className="py-2 pr-4">Type</th><th className="py-2 pr-4">Reason</th>
                  <th className="py-2 pr-4">Locked</th><th className="py-2 pr-4">Status</th><th className="py-2 pr-4"></th>
                </tr></thead>
                <tbody>
                  {locks.map((l) => {
                    const isSelf = selfId && String(l.id) === String(selfId);
                    const status = l.hard ? 'Hard (manual unlock by a different admin)' : l.lockUntil ? `Until ${fmt(l.lockUntil)}` : 'Active';
                    return (
                      <tr key={`${l.type}:${l.id}`} className="border-b border-gray-50">
                        <td className="py-2 pr-4"><div className="text-gray-900">{l.name || l.email || '—'}</div><div className="font-mono text-xs text-gray-400">{l.id}</div></td>
                        <td className="py-2 pr-4 text-gray-600">{l.type}</td>
                        <td className="py-2 pr-4 text-gray-600 max-w-xs truncate" title={l.reason || ''}>{l.reason || '—'}</td>
                        <td className="py-2 pr-4 text-gray-600">{fmt(l.lockedAt)}</td>
                        <td className="py-2 pr-4">{status}</td>
                        <td className="py-2 pr-4">
                          <button
                            disabled={!!isSelf}
                            title={isSelf ? 'You cannot clear your own lock' : 'Unlock'}
                            onClick={() => setConfirmState({
                              title: 'Unlock account', body: `Clear the security lock on ${l.name || l.email || l.id}.`,
                              run: async () => api.security.unlock(l.type, l.id),
                            })}
                            className="px-2.5 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40">
                            {isSelf ? 'Self' : 'Unlock'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ---- Security events ---- */}
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="font-semibold text-gray-900 mb-3">Security events ({events.length})</h2>
          {events.length === 0 ? <div className="text-sm text-gray-400">No security events recorded.</div> : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead><tr className="text-left text-gray-400 border-b">
                  <th className="py-2 pr-3">Time</th><th className="py-2 pr-3">Severity</th><th className="py-2 pr-3">User</th>
                  <th className="py-2 pr-3">Principal</th><th className="py-2 pr-3">IP</th><th className="py-2 pr-3">Level</th>
                  <th className="py-2 pr-3">Action</th><th className="py-2 pr-3">Incident</th><th></th>
                </tr></thead>
                <tbody>
                  {events.map((ev) => {
                    const m = parseMeta(ev.metadata);
                    const open = expanded === ev.id;
                    return (
                      <Fragment key={ev.id}>
                        <tr className="border-b border-gray-50 align-top">
                          <td className="py-2 pr-3 whitespace-nowrap text-gray-600">{fmt(ev.timestamp)}</td>
                          <td className="py-2 pr-3"><span className={`px-2 py-0.5 rounded text-xs ${sevBadge(m.severity)}`}>{m.severity || '—'}</span></td>
                          <td className="py-2 pr-3 text-gray-900">{ev.user_email || ev.entity_name || ev.entity_id || '—'}</td>
                          <td className="py-2 pr-3 text-gray-600">{m.principalType || '—'}</td>
                          <td className="py-2 pr-3 font-mono text-xs text-gray-600">{ev.ip_address || '—'}</td>
                          <td className="py-2 pr-3"><span className={`px-2 py-0.5 rounded text-xs ${levelBadge(m.level)}`}>{m.level || '—'}</span></td>
                          <td className="py-2 pr-3 text-gray-700">{m.action || '—'}</td>
                          <td className="py-2 pr-3 font-mono text-xs text-gray-400">{(m.incidentId || '').slice(0, 8) || '—'}</td>
                          <td className="py-2 pr-3"><button onClick={() => setExpanded(open ? null : ev.id)} className="text-xs text-blue-600">{open ? 'Hide' : 'Details'}</button></td>
                        </tr>
                        {open && (
                          <tr className="bg-gray-50">
                            <td colSpan={9} className="p-3">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                <div><span className="text-gray-400">Triggering behavior:</span> <span className="text-gray-800">{(m.signals || []).join('; ') || ev.changes_summary || '—'}</span></div>
                                <div><span className="text-gray-400">Resource classes:</span> <span className="text-gray-800">{(m.classes || []).join(', ') || '—'}</span></div>
                                <div><span className="text-gray-400">Window counts:</span> <span className="text-gray-800 font-mono">{m.windowCounts ? JSON.stringify(m.windowCounts) : '—'}</span></div>
                                <div><span className="text-gray-400">Incident:</span> <span className="text-gray-800 font-mono">{m.incidentId || '—'}</span></div>
                                <div><span className="text-gray-400">Session hash:</span> <span className="text-gray-800 font-mono">{m.sessionHash || '—'}</span></div>
                                <div><span className="text-gray-400">Mode:</span> <span className="text-gray-800">{m.mode || '—'}</span></div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ---- Emergency recovery guidance ---- */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <h2 className="font-semibold text-gray-900 mb-1">Emergency administrator recovery</h2>
          <p className="text-sm text-gray-600">
            If an administrator is locked out and no other administrator can clear the lock, recovery is performed through a
            restricted <strong>server-side procedure</strong> run with database access by authorized operations staff. It requires
            an explicit actor and reason and is fully audited. This procedure is intentionally <strong>not</strong> available through
            the web interface, and no recovery credentials are shown here.
          </p>
        </div>
      </>
      )}

      {/* ---- Confirmation modal ---- */}
      {confirmState && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => !busy && setConfirmState(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className={`text-lg font-semibold ${confirmState.danger ? 'text-red-700' : 'text-gray-900'}`}>{confirmState.title}</h3>
            <p className="mt-2 text-sm text-gray-600">{confirmState.body}</p>
            {confirmState.requireReason && (
              <input autoFocus value={confirmReason} onChange={(e) => setConfirmReason(e.target.value)} placeholder="Reason (required)"
                className="mt-3 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button disabled={busy} onClick={() => { setConfirmState(null); setConfirmReason(''); }} className="px-3 py-2 text-sm rounded-lg border border-gray-300">Cancel</button>
              <button
                disabled={busy || (confirmState.requireReason && !confirmReason.trim())}
                onClick={() => doAction(() => confirmState.run(confirmReason.trim() || mReason.trim()))}
                className={`px-3 py-2 text-sm rounded-lg text-white disabled:opacity-40 ${confirmState.danger ? 'bg-red-600' : 'bg-blue-600'}`}>
                {busy ? 'Working…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
